import * as React from "react";
import { Link } from "react-router-dom";
import { Role } from "webportalclient";
import { useQuery } from "../../Components/Query";
import { Column, Table } from "../../Components/QueryTable";
import { SortHeader } from "../../Components/SortHeader";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { Authorization } from "../../misc/components/Authorization";
import { HamburgerMenu } from "../../misc/components/HamburgerMenu";
import { FileDownload } from "../../misc/FileDownload";
import { ImportPermissions } from "./ImportPermissions";
import { ReportsPermissions } from "./ReportsPermissions";
import Condition from "../../misc/components/Condition";
import { SSRSCredential } from "../../misc/api/apiTypes";

export const RolesTable = () => {
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const { data, refresh } = useQuery();
    const [importPermissionsVisible, setImportPermissionsVisible] = React.useState<{ visible: boolean; roleId: string }>({ visible: false, roleId: null });
    const [reportsPermissionModalVisible, setReportsPermissionModalVisible] = React.useState<{ visible: boolean; roleId: string }>({ visible: false, roleId: null });
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();

    const [enableReportsAction, setEnableReportsAction] = React.useState<boolean>(false)

    const exportPermissions = async (roleId: string) => {
        const roleResult = await client.client.getRole(roleId, "name, permissionsIds");

        const exportModel = [{
            RoleName: roleResult.name,
            RolePermissions: roleResult.permissionsIds,
        }]

        const exportBlob = new Blob([JSON.stringify(exportModel)]);

        FileDownload(exportBlob, `role_${roleResult.name}_permissions_${new Date().toISOString()}.json`);
    }


    React.useEffect(() => {
        legacyClient.getReportCredentials().then(result =>
            setEnableReportsAction((result as SSRSCredential).ServerType == 2)
        )
    }, [study])

    const exportPermissionsAll = async () => {
        const rolesResult = await client.client.getRoles("name, permissionsIds", `studyId eq ${study.id}`);

        const exportModel = rolesResult.value.map(r => ({
            RoleName: r.name,
            RolePermissions: r.permissionsIds,
        }));

        const exportBlob = new Blob([JSON.stringify(exportModel)]);

        FileDownload(exportBlob, `role_all_permissions_${new Date().toISOString()}.json`);
    }

    const menuFormatter = (value: Role) => {
        return (
            <HamburgerMenu>
                <Authorization permissionId={PermissionEnum.AlignPermissionsToRoles} studyId={study.id} currentPermissions={currentUserPermissions}>
                    <li>
                        <Link to={`/rolemanagement/permissions/${value.id}`}>Align Permissions</Link>
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.ViewRoleManagement}>
                            <Link to={`/rolemanagement/roles/${value.id}`}>Align Roles</Link>
                        </Authorization>
                        <a onClick={() => exportPermissions(value.id)}>Export Permissions</a>
                        {
                            enableReportsAction ? 
                            <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.ViewReports}>
                                    <a onClick={() => setReportsPermissionModalVisible({ visible: true, roleId: value.id })}>Reports Permissions</a>
                            </Authorization> 
                            : null
                        }
                        <a onClick={() => setImportPermissionsVisible({ visible: true, roleId: value.id })}>Import Permissions</a>
                    </li>
                </Authorization>
            </HamburgerMenu>
        );
    }

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Role Management</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.CreateRole}>
                            <Link id="createNew" className="btn btn-primary" to="/rolemanagement/create">
                                Create New
                            </Link>
                        </Authorization>&nbsp;
                        <a onClick={() => exportPermissionsAll()} className="btn btn-primary btn-outline">
                            Export All
                        </a>&nbsp;
                        <a className="btn btn-primary btn-outline" onClick={() => setImportPermissionsVisible({ visible: true, roleId: null })}>
                            Import All
                        </a>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div className="table-responsive" data-automationid="RoleManagementTable">
                                <Table data={data}>
                                    <Column field="name" header={<SortHeader title="Role" field="name" />} formatter={n => n} />
                                    <Column field="users@odata.count" header="Number of Users within Role" formatter={uc => uc} />
                                    <Column field="permissions@odata.count" header="Number of Permissions" formatter={pc => pc} />
                                    {
                                        enableReportsAction ? <Column field="reports" header="Number of Reports" formatter={pc => pc.length} />
                                            : null
                                    }
                                    <Column field="" formatter={menuFormatter} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {importPermissionsVisible.visible ? <ImportPermissions
                roleId={importPermissionsVisible.roleId}
                onClose={() => {
                    setImportPermissionsVisible({ roleId: null, visible: false });
                    refresh();
                }}
            /> : null}
            {reportsPermissionModalVisible.visible ? <ReportsPermissions
                roleId={reportsPermissionModalVisible.roleId}
                onClose={() => {
                    setReportsPermissionModalVisible({ roleId: null, visible: false });
                    refresh();
                }}
            /> : null}
        </>
    );
}
